import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  RefreshCcw, 
  Printer, 
  Edit2, 
  X, 
  PlusCircle, 
  Image as ImageIcon, 
  Phone, 
  Hash, 
  DollarSign, 
  CreditCard, 
  Smartphone, 
  ChevronRight, 
  Check, 
  Trash2, 
  Calendar,
  Clock,
  Tag,
  Percent,
  TrendingUp,
  Receipt,
  CheckCircle,
  ChefHat,
  Sofa,
  UtensilsCrossed,
} from 'lucide-react';
import { useAuth } from '../../common/context/AuthContext';
import { API_URL } from '../../api/config';

const MODAL_TYPES = {
  EDIT: 'edit',
  MENU: 'menu',
  VARIANT: 'variant',
  PAYMENT: 'payment',
  DETAILS: 'details'
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { userToken } = useAuth();
  
  // Main state
  const [loading, setLoading] = useState(true);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [restaurantInfo, setRestaurantInfo] = useState(null);
  const [bills, setBills] = useState([]);
  const [todayRevenue, setTodayRevenue] = useState(0);
  const [products, setProducts] = useState([]);
  const [tables, setTables] = useState([]);
  const [occupiedTables, setOccupiedTables] = useState(0);
  
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectedBill, setSelectedBill] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedVariants, setSelectedVariants] = useState([]);
  const [discountType, setDiscountType] = useState('percentage');
  const [discountAmount, setDiscountAmount] = useState(0);
  const [activeModal, setActiveModal] = useState(null);
  const [discountPercentage, setDiscountPercentage] = useState(0);

  // Helper function to safely get field value
  const getField = (obj, fieldName) => {
    if (!obj) return null;
    return obj[fieldName] ?? null;
  };

  const fetchRestaurantInfo = async () => {
    try {
      const response = await fetch(`${API_URL}/settings`, {
        headers: {
          'Authorization': `Bearer ${userToken}`,
          'Accept': 'application/json'
        }
      });
      
      if (!response.ok) throw new Error('Failed to fetch restaurant info');

      const data = await response.json();
      setRestaurantInfo({
        name: data.restaurantName || data.restaurant_name,
        logoUrl: data.logoUrl || data.logo_url,
        address: data.location,
        phoneNumber: data.phoneNumber || data.phone_number,
        email: data.businessEmail || data.business_email,
        gstin: data.gstin
      });
    } catch (error) {
      console.error('Failed to fetch restaurant info:', error);
      alert('Error: Failed to load restaurant information');
    }
  };

  const fetchTables = async () => {
    try {
      const response = await fetch(`${API_URL}/tables`, {
        headers: {
          'Authorization': `Bearer ${userToken}`,
          'Accept': 'application/json'
        }
      });
      
      if (!response.ok) throw new Error('Failed to fetch tables');
      const data = await response.json();
      setTables(Array.isArray(data) ? data : []);
      
      // Count occupied tables
      const occupied = Array.isArray(data) ? data.filter(table => table.status?.toUpperCase() === 'OCCUPIED' || table.isOccupied).length : 0;
      setOccupiedTables(occupied);
    } catch (error) {
      console.error('Failed to fetch tables:', error);
      setTables([]);
    }
  };

  const getTableDisplayName = (tableId, tableNumber) => {
    if (!tableId) {
      return tableNumber ? `Table ${tableNumber}` : 'No Table';
    }
    
    if (!tables || !Array.isArray(tables) || tables.length === 0) {
      return tableNumber ? `Table ${tableNumber}` : 'Unknown Table';
    }
    
    const table = tables.find(t => t.id === tableId);
    
    if (!table) {
      return tableNumber ? `Table ${tableNumber}` : 'Unknown Table';
    }
    
    const tName = table.tableName || 'Table';
    const tNum = table.tableNumber;
    
    return tNum ? `${tName} (#${tNum})` : tName;
  };

  const renderDiscountBadge = (bill) => {
    const dType = bill.discountType;
    const dPercentage = bill.discountPercentage || 0;
    const dAmount = bill.discountAmount || 0;

    if (dType === 'percentage' && dPercentage > 0) {
      return (
        <div className="flex items-center gap-1 bg-orange-50 text-orange-600 px-2 py-1 rounded-full text-xs font-bold border border-orange-100">
          <Percent size={12} />
          <span>{dPercentage}% OFF</span>
        </div>
      );
    } else if (dType === 'amount' && dAmount > 0) {
      return (
        <div className="flex items-center gap-1 bg-emerald-50 text-emerald-600 px-2 py-1 rounded-full text-xs font-bold border border-emerald-100">
          <span>₹{dAmount} OFF</span>
        </div>
      );
    }
    return null;
  };

  const isBillEditable = (bill) => {
    const createdAt = bill.createdAt;
    if (!createdAt) return false;
    
    const billDate = new Date(createdAt);
    const currentDate = new Date();
    const timeDifferenceInMinutes = (currentDate - billDate) / (1000 * 60);
    
    return timeDifferenceInMinutes <= 15;
  };

  const fetchTodaysBills = async () => {
    if (!userToken) return;

    try {
      // Use local midnight to correctly capture today's bills in IST/local timezone
      const today = new Date();
      const localMidnight = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0, 0);
      const endOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59, 999);
      
      const queryParams = new URLSearchParams({
        startDate: localMidnight.toISOString(),
        endDate: endOfDay.toISOString(),
        limit: 100,
        page: 1
      }).toString();

      const response = await fetch(`${API_URL}/bills?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${userToken}`,
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch bills');
      }

      const data = await response.json();
      
      let allBills = data.bills || [];
      
      if (data.totalPages > 1) {
        const additionalPages = [];
        for (let page = 2; page <= Math.min(data.totalPages, 10); page++) {
          const pageParams = new URLSearchParams({
            startDate: today.toISOString(),
            endDate: new Date().toISOString(),
            limit: 100,
            page
          }).toString();
          
          additionalPages.push(
            fetch(`${API_URL}/bills?${pageParams}`, {
              headers: {
                'Authorization': `Bearer ${userToken}`,
                'Accept': 'application/json'
              }
            }).then(res => res.json())
          );
        }
        
        const additionalData = await Promise.all(additionalPages);
        additionalData.forEach(pageData => {
          allBills = [...allBills, ...(pageData.bills || [])];
        });
      }
      
      allBills = allBills.map(bill => ({
        ...bill,
        id: bill.id || bill._id,
        _id: bill._id || bill.id
      }));
      
      setBills(allBills);
      
      const revenue = allBills.reduce((total, bill) => {
        return total + (bill.status === 'COMPLETED' ? (bill.totalAmount || 0) : 0);
      }, 0);
      
      setTodayRevenue(revenue);
    } catch (error) {
      console.error('Failed to fetch bills:', error);
      alert(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

const normalizeProduct = (product) => {
  return {
    ...product,
    id: product.id || product._id,
    _id: product.id || product._id,
    // CRITICAL: Map snake_case to camelCase
    pricingType: product.pricing_type || product.pricingType || 'basePrice',
    itemName: product.item_name || product.itemName,
    item_name: product.item_name || product.itemName,
    basePrice: product.base_price || product.basePrice,
    sellingPrice: product.selling_price || product.sellingPrice,
    imageUrl: product.image_url || product.imageUrl,
    inStock: product.in_stock !== undefined ? product.in_stock : product.inStock,
    unitType: product.unit_type || product.unitType,
  };
};

// Update fetchProducts function
const fetchProducts = async () => {
  setLoadingProducts(true);
  try {
    const response = await fetch(`${API_URL}/products`, {
      headers: {
        'Authorization': `Bearer ${userToken}`,
        'Accept': 'application/json'
      }
    });

    if (!response.ok) throw new Error('Failed to fetch products');
    const responseData = await response.json();
    
    let productsData = [];
    if (responseData.success && Array.isArray(responseData.data)) {
      productsData = responseData.data;
    } else if (Array.isArray(responseData)) {
      productsData = responseData;
    } else {
      productsData = [];
    }
    
    // CRITICAL: Normalize all products
    const normalizedProducts = productsData.map(normalizeProduct);
    
    console.log('=== DASHBOARD PRODUCTS LOADED ===');
    normalizedProducts.forEach(p => {
      console.log(`${p.item_name}: pricingType = ${p.pricingType}`);
    });
    
    setProducts(normalizedProducts);
  } catch (error) {
    console.error('Failed to fetch products:', error);
    setProducts([]);
    alert('Error: Failed to load menu items');
  } finally {
    setLoadingProducts(false);
  }
};

// Update handleItemSelect to preserve pricingType
const handleItemSelect = (product) => {
  setSelectedProduct(product);
  
  console.log('Selected product:', {
    name: product.item_name,
    pricingType: product.pricingType,
    hasVariants: product.variants?.length > 0
  });
  
  if (product.variants && Array.isArray(product.variants) && product.variants.length > 0) {
    setSelectedVariants([]);
    setActiveModal(MODAL_TYPES.VARIANT);
  } else {
    const price = product.pricingType === 'basePrice' ? product.basePrice : product.sellingPrice;
    addItemToSelection({
      _id: product.id,
      item_name: product.item_name,
      price: price,
      pricingType: product.pricingType, // CRITICAL: Include pricingType
      category: product.category,
      quantity: 1
    });
  }
};

// Update handleVariantSelection to preserve pricingType
const handleVariantSelection = (variant) => {
  const newVariant = {
    _id: selectedProduct.id,
    item_name: `${selectedProduct.item_name} - ${variant.name}`,
    price: variant.price,
    variant,
    pricingType: selectedProduct.pricingType, // CRITICAL: Include pricingType
    category: selectedProduct.category,
    quantity: 1
  };
  
  console.log('Creating variant item:', {
    name: newVariant.item_name,
    pricingType: newVariant.pricingType,
    price: newVariant.price
  });
  
  setSelectedVariants(prev => {
    const existingIndex = prev.findIndex(v => 
      v._id === newVariant._id && v.variant.name === variant.name
    );
    
    if (existingIndex >= 0) {
      const updated = [...prev];
      updated.splice(existingIndex, 1);
      return updated;
    }
    return [...prev, newVariant];
  });
};

  const handleVariantsConfirm = () => {
    selectedVariants.forEach(addItemToSelection);
  };

  const addItemToSelection = (item) => {
    setSelectedItems(prev => {
      const existingItem = prev.find(i => 
        i._id === item._id && i.variant?.name === item.variant?.name
      );

      if (existingItem) {
        return prev.map(i => 
          i._id === item._id && i.variant?.name === item.variant?.name
            ? { ...i, quantity: i.quantity + 1 }
            : i
        );
      }
      return [...prev, item];
    });
  };

const handleDeleteItem = (itemToDelete, itemIndex) => {
  if (window.confirm("Are you sure you want to remove this item?")) {
    setSelectedItems(prev => prev.filter((item, index) => {
      // Use index for unique identification when editing
      return index !== itemIndex;
    }));
  }
};

  const updatePaymentMethod = async (billId, newPaymentMethod) => {
    const bill = bills.find(b => b.id === billId);
    
    if (!bill || !isBillEditable(bill)) {
      alert('Error: This bill can no longer be edited');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/bills/${bill.id}/payment-method`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${userToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ paymentMethod: newPaymentMethod }),
      });
  
      if (!response.ok) throw new Error('Failed to update payment method');
      
      await fetchTodaysBills();
      setActiveModal(null);
    } catch (error) {
      console.error('Failed to update payment method:', error);
      alert('Error: Failed to update payment method');
    }
  };

const handleEditBill = (bill) => {
  console.log('=== EDITING BILL ===');
  console.log('Bill items:', bill.items);
  
  setSelectedBill(bill);
  
  // CRITICAL: Ensure items have pricingType when loading from existing bill
  const itemsWithPricingType = (bill.items || []).map(item => ({
    ...item,
    pricingType: item.pricingType || 'basePrice', // Preserve existing or default
    _id: item.productId || item._id || item.id
  }));
  
  console.log('Items with pricingType:', itemsWithPricingType);
  
  setSelectedItems(itemsWithPricingType);
  setActiveModal(MODAL_TYPES.EDIT);
};

  const handleBillDetails = (bill) => {
    setSelectedBill(bill);
    
    if (bill.discountType === 'amount') {
      setDiscountType('amount');
      setDiscountPercentage(0);
      setDiscountAmount(bill.discountAmount || 0);
    } else {
      setDiscountType('percentage');
      setDiscountPercentage(bill.discountPercentage || 0);
      setDiscountAmount(0);
    }
    setActiveModal(MODAL_TYPES.DETAILS);
  };

  const handleDeleteBill = async (bill) => {
    if (!isBillEditable(bill)) {
      alert('Error: This bill can only be deleted within 15 minutes of creation');
      return;
    }

    const confirmMessage = `Are you sure you want to delete Bill #${bill.billNumber}?\n\nAmount: ₹${bill.totalAmount.toFixed(2)}\nThis action cannot be undone.`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/bills/${bill.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${userToken}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete bill');
      }

      await fetchTodaysBills();
      alert('Success: Bill deleted successfully');
    } catch (error) {
      console.error('Failed to delete bill:', error);
      alert(`Error: ${error.message}`);
    }
  };

  const handlePrint = async (bill) => {
    try {
      if (!bill.id) {
        alert('Error: Bill ID is missing');
        console.error('Bill object:', bill);
        return;
      }

      navigate(`/print-bill/${bill.id}`, {
        state: { 
          restaurantInfo: restaurantInfo
        }
      });
      
    } catch (error) {
      console.error('Failed to navigate to print screen:', error);
      alert('Error: Failed to open print screen');
    }
  };

const handleSaveBillChanges = async () => {
  if (!selectedBill) return;

  // Don't allow saving if no items
  if (selectedItems.length === 0) {
    alert('Error: Cannot save bill with no items. Please add at least one item or delete the bill.');
    return;
  }

  try {
    console.log('=== SAVING BILL CHANGES ===');
    console.log('Selected Items:');
    selectedItems.forEach(item => {
      console.log(`  ${item.item_name}: pricingType = ${item.pricingType}, price = ${item.price}`);
    });
    
    // Recalculate totals based on selected items
    let basePriceSubtotal = 0;
    let mrpSubtotal = 0;
    
    selectedItems.forEach(item => {
      const price = item.selectedVariant ? item.selectedVariant.price : item.price;
      const itemTotal = price * (item.quantity || 1);
      
      const pricingType = item.pricingType || 'basePrice';
      
      if (pricingType === 'basePrice') {
        basePriceSubtotal += itemTotal;
        console.log(`  - Added to BASE PRICE: ${itemTotal}`);
      } else {
        mrpSubtotal += itemTotal;
        console.log(`  - Added to MRP: ${itemTotal}`);
      }
    });
    
    const newSubtotal = basePriceSubtotal + mrpSubtotal;
    const newCgst = basePriceSubtotal * 0.025; // 2.5% only on base price items
    const newSgst = basePriceSubtotal * 0.025; // 2.5% only on base price items
    
    console.log('Calculated totals:');
    console.log(`  Base Price Subtotal: ${basePriceSubtotal}`);
    console.log(`  MRP Subtotal: ${mrpSubtotal}`);
    console.log(`  Total Subtotal: ${newSubtotal}`);
    console.log(`  CGST: ${newCgst}`);
    console.log(`  SGST: ${newSgst}`);
    
    // Keep existing discount
    const existingDiscountType = selectedBill.discountType;
    const existingDiscountPercentage = selectedBill.discountPercentage || 0;
    const existingDiscountAmount = selectedBill.discountAmount || 0;
    
    // Recalculate discount based on new totals
    let newDiscountAmount = 0;
    if (existingDiscountType === 'percentage' && existingDiscountPercentage > 0) {
      const totalBeforeDiscount = newSubtotal + newCgst + newSgst;
      newDiscountAmount = (totalBeforeDiscount * existingDiscountPercentage) / 100;
    } else if (existingDiscountType === 'amount' && existingDiscountAmount > 0) {
      const totalBeforeDiscount = newSubtotal + newCgst + newSgst;
      newDiscountAmount = Math.min(existingDiscountAmount, totalBeforeDiscount);
    }
    
    const newTotalAmount = Math.max(0, newSubtotal + newCgst + newSgst - newDiscountAmount);

    const requestBody = {
      items: selectedItems.map(item => ({
        productId: item._id || item.id,
        item_name: item.item_name,
        quantity: item.quantity,
        price: item.price,
        variant: item.selectedVariant,
        pricingType: item.pricingType || 'basePrice' // CRITICAL: Include pricingType
      })),
      subtotal: newSubtotal,
      cgst: newCgst,
      sgst: newSgst,
      totalAmount: newTotalAmount,
      discountAmount: newDiscountAmount
    };
    
    console.log('Request body:', JSON.stringify(requestBody, null, 2));

    const response = await fetch(`${API_URL}/bills/${selectedBill.id}`, {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${userToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) throw new Error('Failed to update bill');
    
    await fetchTodaysBills();
    setActiveModal(null);
    setSelectedItems([]);
    alert('Success: Bill updated successfully');
  } catch (error) {
    console.error('Failed to update bill:', error);
    alert('Error: Failed to update bill');
  }
};

const handleSaveBillDetails = async () => {
  if (!selectedBill) return;

  try {
    // Recalculate based on bill items and their pricing types
    let basePriceSubtotal = 0;
    let mrpSubtotal = 0;
    
    selectedBill.items.forEach(item => {
      const price = item.variant ? item.variant.price : item.price;
      const itemTotal = price * (item.quantity || 1);
      
      if (item.pricingType === 'basePrice') {
        basePriceSubtotal += itemTotal;
      } else {
        mrpSubtotal += itemTotal;
      }
    });
    
    const subtotal = basePriceSubtotal + mrpSubtotal;
    const cgst = basePriceSubtotal * 0.025; // Only on base price items
    const sgst = basePriceSubtotal * 0.025; // Only on base price items
    const totalBeforeDiscount = subtotal + cgst + sgst;
    
    let newDiscountAmount = 0;
    let newDiscountPercentage = 0;
    
    if (discountType === 'percentage') {
      newDiscountAmount = (totalBeforeDiscount * discountPercentage) / 100;
      newDiscountPercentage = discountPercentage;
    } else if (discountType === 'amount') {
      newDiscountAmount = Math.min(discountAmount, totalBeforeDiscount);
      newDiscountPercentage = 0;
    }
    
    const newTotalAmount = Math.max(0, totalBeforeDiscount - newDiscountAmount);

    const response = await fetch(`${API_URL}/bills/${selectedBill.id}`, {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${userToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        discountType: discountType,
        discountPercentage: newDiscountPercentage,
        discountAmount: newDiscountAmount,
        totalAmount: newTotalAmount,
      }),
    });

    if (!response.ok) throw new Error('Failed to update bill details');
    
    await fetchTodaysBills();
    setActiveModal(null);
    alert('Success: Bill details updated successfully');
  } catch (error) {
    console.error('Failed to update bill details:', error);
    alert('Error: Failed to update bill details');
  }
};

  const handleNewSale = () => {
    navigate('/new-bill');
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true
    }).format(date);
  };

  const completedBills = bills.filter(bill => bill.status === 'COMPLETED').length;
  
  const averageTransaction = completedBills > 0 
    ? todayRevenue / completedBills 
    : 0;
useEffect(() => {
  const loadData = async () => {
    await fetchRestaurantInfo();
    await fetchTables();
    await fetchTodaysBills(); 
  };
  
  loadData();
}, []);

  if (loading) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-[#4F46E5]"></div>
      </div>
    );
  }

  const currentDate = new Date().toLocaleDateString('en-US', { 
    weekday: 'long', 
    month: 'short', 
    day: 'numeric' 
  });

  // Render methods for modals
  const renderPaymentMethodModal = () => (
    activeModal === MODAL_TYPES.PAYMENT && (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end sm:items-center justify-center">
        <div className="bg-white w-full max-w-md sm:rounded-2xl rounded-t-2xl overflow-hidden shadow-xl transform transition-all">
          <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center">
            <h3 className="text-lg font-bold text-slate-800">Update Payment Method</h3>
            <button 
              onClick={() => setActiveModal(null)}
              className="text-slate-400 hover:text-slate-600 focus:outline-none"
            >
              <X size={20} />
            </button>
          </div>
          
          <div className="px-6 py-4 max-h-96 overflow-y-auto">
            {['CASH', 'CARD', 'UPI'].map((method) => (
              <button
                key={method}
                className="flex justify-between items-center w-full px-6 py-4 text-left hover:bg-slate-50 border border-slate-100 rounded-xl mb-3 transition-all group"
                onClick={() => updatePaymentMethod(selectedBill.id, method)}
              >
                <div className="flex items-center">
                  {method === 'CASH' ? (
                    <div className="p-3 bg-emerald-50 text-emerald-600 rounded-xl mr-4">
                      <DollarSign size={24} />
                    </div>
                  ) : method === 'CARD' ? (
                    <div className="p-3 bg-indigo-50 text-[#4F46E5] rounded-xl mr-4">
                      <CreditCard size={24} />
                    </div>
                  ) : (
                    <div className="p-3 bg-purple-50 text-purple-600 rounded-xl mr-4">
                      <Smartphone size={24} />
                    </div>
                  )}
                  <div>
                    <span className="text-slate-800 font-bold text-lg">{method}</span>
                    <p className="text-slate-400 text-sm">Tap to set as payment method</p>
                  </div>
                </div>
                <ChevronRight size={20} className="text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
              </button>
            ))}
          </div>
        </div>
      </div>
    )
  );

  const renderVariantModal = () => (
    activeModal === MODAL_TYPES.VARIANT && selectedProduct && (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end sm:items-center justify-center">
        <div className="bg-white w-full max-w-md sm:rounded-2xl rounded-t-2xl overflow-hidden shadow-xl transform transition-all">
          <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center">
            <h3 className="text-lg font-bold text-slate-800">Select Variants</h3>
            <button 
              onClick={() => setActiveModal(activeModal === MODAL_TYPES.MENU ? MODAL_TYPES.MENU : MODAL_TYPES.EDIT)}
              className="text-slate-400 hover:text-slate-600 focus:outline-none"
            >
              <X size={20} />
            </button>
          </div>
          
          <div className="px-6 py-4 max-h-96 overflow-y-auto">
            <h4 className="text-xl font-extrabold text-slate-900 mb-4">
              {selectedProduct.item_name}
            </h4>
            
            <div className="space-y-3 mb-6">
              {(selectedProduct.variants || []).map((variant) => {
                const isSelected = selectedVariants.some(v => 
                  v._id === selectedProduct.id && v.variant.name === variant.name
                );
                
                return (
                  <button
                    key={variant.name}
                    className={`flex justify-between items-center w-full px-5 py-4 rounded-xl text-left transition-all border ${
                      isSelected 
                        ? 'bg-[#4F46E5] text-white border-[#4F46E5] shadow-lg shadow-indigo-100' 
                        : 'bg-white text-slate-800 border-slate-200 hover:border-slate-300 hover:shadow-sm'
                    }`}
                    onClick={() => handleVariantSelection(variant)}
                  >
                    <div>
                      <span className="font-bold text-lg">{variant.name}</span>
                      <p className={`text-sm mt-1 ${isSelected ? 'text-indigo-100' : 'text-slate-500'}`}>
                        ₹{variant.price.toFixed(2)}
                      </p>
                    </div>
                    {isSelected && <Check size={20} />}
                  </button>
                );
              })}
            </div>
            
            <button
              className={`w-full py-4 px-6 rounded-xl font-bold text-white shadow-lg transition-all transform ${
                selectedVariants.length > 0 
                  ? 'bg-[#4F46E5] hover:bg-[#4338CA] shadow-indigo-100 hover:scale-[1.02] active:scale-95' 
                  : 'bg-slate-300 cursor-not-allowed'
              }`}
              onClick={() => {
                handleVariantsConfirm();
                setActiveModal(activeModal === MODAL_TYPES.MENU ? MODAL_TYPES.MENU : MODAL_TYPES.EDIT);
              }}
              disabled={selectedVariants.length === 0}
            >
              Add Selected Variants
            </button>
          </div>
        </div>
      </div>
    )
  );
  
const renderEditBillModal = () => (
  activeModal === MODAL_TYPES.EDIT && (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end sm:items-center justify-center">
      <div className="bg-white w-full max-w-lg sm:rounded-2xl rounded-t-2xl overflow-hidden shadow-xl transform transition-all sm:max-h-[90vh]">
        <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center">
          <h3 className="text-lg font-bold text-slate-800">
            Edit Bill #{selectedBill?.billNumber}
          </h3>
          <button 
            onClick={() => setActiveModal(null)}
            className="text-slate-400 hover:text-slate-600 focus:outline-none"
          >
            <X size={20} />
          </button>
        </div>
        
        <div className="px-6 py-4 max-h-[calc(90vh-10rem)] overflow-y-auto">
          {selectedItems.length === 0 ? (
            <div className="py-12 text-center text-slate-500">
              <ImageIcon size={48} className="mx-auto mb-4 text-slate-300" />
              <p className="text-lg font-semibold">No items added to this bill.</p>
              <p className="text-sm text-slate-400 mt-1">Add items using the button below.</p>
            </div>
          ) : (
            <div className="space-y-3 mb-6">
              {selectedItems.map((item, index) => (
                <div key={`item-${index}-${item._id}`} className="flex justify-between items-center p-4 bg-slate-50 hover:bg-slate-100 rounded-xl border border-slate-100 transition-all group">
                  <div>
                    <h4 className="font-bold text-slate-800">{item.item_name}</h4>
                    {item.selectedVariant && (
                      <p className="text-xs text-blue-600 font-semibold mt-1">
                        {item.selectedVariant.name}
                      </p>
                    )}
                    <p className="text-sm text-slate-600 mt-1">
                      <span className="font-bold">{item.quantity}</span> x ₹{item.price.toFixed(2)} = ₹{(item.quantity * item.price).toFixed(2)}
                    </p>
                  </div>
                  <button
                    onClick={() => handleDeleteItem(item, index)}
                    className="text-slate-400 hover:text-rose-500 hover:bg-rose-50 p-2 rounded-full transition-all opacity-60 group-hover:opacity-100"
                  >
                    <Trash2 size={20} />
                  </button>
                </div>
              ))}
            </div>
          )}
          
          <div className="flex flex-col space-y-3">
            <button 
              className="w-full py-4 px-6 bg-slate-50 text-slate-700 rounded-xl font-bold hover:bg-slate-100 flex justify-center items-center transition-all border border-slate-200"
              onClick={() => {
                setActiveModal(MODAL_TYPES.MENU);
                fetchProducts();
              }}
            >
              <PlusCircle size={22} className="mr-2" />
              Add Items
            </button>
            
            <button 
              className="w-full py-4 px-6 bg-[#4F46E5] hover:bg-[#4338CA] text-white rounded-xl font-bold shadow-lg shadow-indigo-100 transition-all transform hover:scale-[1.02] active:scale-95"
              onClick={handleSaveBillChanges}
              disabled={selectedItems.length === 0}
            >
              Save Changes
            </button>
          </div>
        </div>
      </div>
    </div>
  )
);
  
  const renderMenuModal = () => (
    activeModal === MODAL_TYPES.MENU && (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end sm:items-center justify-center">
        <div className="bg-white w-full max-w-lg sm:rounded-2xl rounded-t-2xl overflow-hidden shadow-xl transform transition-all sm:max-h-[90vh]">
          <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center">
            <h3 className="text-lg font-bold text-slate-800">Select Menu Items</h3>
            <button 
              onClick={() => setActiveModal(MODAL_TYPES.EDIT)}
              className="text-slate-400 hover:text-slate-600 focus:outline-none"
            >
              <X size={20} />
            </button>
          </div>
          
          <div className="overflow-y-auto max-h-[calc(90vh-10rem)]">
            {loadingProducts ? (
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-t-3 border-b-3 border-[#4F46E5]"></div>
              </div>
            ) : products.length === 0 ? (
              <div className="flex flex-col justify-center items-center py-12 text-slate-500">
                <ImageIcon size={48} className="mb-4 text-slate-300" />
                <p className="text-lg font-bold">No products available</p>
                <p className="text-sm text-slate-400 mt-1">Add products first in the Products page</p>
              </div>
            ) : (
              <div className="p-2">
                {products.map(item => (
                  <button 
                    key={item.id}
                    className="w-full text-left px-6 py-5 border-b border-slate-100 hover:bg-slate-50 transition-all last:border-0"
                    onClick={() => handleItemSelect(item)}
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <span className="font-bold text-slate-800 text-lg">{item.item_name || 'Unknown Item'}</span>
                        {item.description && (
                          <p className="text-slate-500 text-sm mt-1">{item.description}</p>
                        )}
                      </div>
                      <div className="text-right">
                        {item.variants && Array.isArray(item.variants) && item.variants.length > 0 ? (
                          <div className="bg-indigo-50 text-[#4F46E5] px-3 py-1 rounded-full text-xs font-bold border border-indigo-100">
                            {item.variants.length} variants
                          </div>
                        ) : (
                          <div className="font-bold text-slate-900">
                            ₹{(item.pricingType === 'basePrice' ? item.basePrice : item.sellingPrice).toFixed(2)}
                          </div>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
          
          <div className="px-6 py-4 border-t border-slate-100">
            <button 
              className="w-full py-4 px-6 bg-[#4F46E5] hover:bg-[#4338CA] text-white rounded-xl font-bold shadow-lg shadow-indigo-100 transition-all transform hover:scale-[1.02] active:scale-95"
              onClick={() => setActiveModal(MODAL_TYPES.EDIT)}
            >
              Done Adding Items
            </button>
          </div>
        </div>
      </div>
    )
  );

  const renderDetailsModal = () => (
    activeModal === MODAL_TYPES.DETAILS && selectedBill && (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end sm:items-center justify-center">
        <div className="bg-white w-full max-w-md sm:rounded-2xl rounded-t-2xl overflow-hidden shadow-xl transform transition-all">
          <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center">
            <h3 className="text-lg font-bold text-slate-800">Bill Details #{selectedBill.billNumber}</h3>
            <button
              onClick={() => setActiveModal(null)}
              className="text-slate-400 hover:text-slate-600 focus:outline-none"
            >
              <X size={20} />
            </button>
          </div>
          
          <div className="px-6 py-4 max-h-96 overflow-y-auto">
            <div className="mb-4">
              <label className="block text-sm font-bold text-slate-700 mb-2">
                Discount Type:
              </label>
              <div className="flex gap-2">
                <button
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl border transition-all font-bold ${
                    discountType === 'percentage'
                      ? 'bg-[#4F46E5] text-white border-[#4F46E5] shadow-lg shadow-indigo-100'
                      : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                  }`}
                  onClick={() => {
                    setDiscountType('percentage');
                    setDiscountAmount(0);
                  }}
                >
                  <Tag size={16} />
                  <span>Percentage</span>
                </button>
                <button
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl border transition-all font-bold ${
                    discountType === 'amount'
                      ? 'bg-emerald-500 text-white border-emerald-500 shadow-lg shadow-emerald-100'
                      : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                  }`}
                  onClick={() => {
                    setDiscountType('amount');
                    setDiscountPercentage(0);
                  }}
                >
                  <DollarSign size={16} />
                  <span>Cash Amount</span>
                </button>
              </div>
            </div>

            {discountType === 'percentage' ? (
              <div className="mb-6">
                <label className="block text-sm font-bold text-slate-700 mb-2">
                  Discount Percentage (%)
                </label>
                <div className="relative">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={discountPercentage}
                    onChange={(e) => {
                      const num = Number(e.target.value);
                      if (!isNaN(num) && num >= 0 && num <= 100) {
                        setDiscountPercentage(num);
                      }
                    }}
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-[#4F46E5] focus:border-transparent outline-none font-medium"
                    placeholder="e.g., 10"
                  />
                  <div className="absolute right-3 top-3 text-slate-400 font-bold">%</div>
                </div>
              </div>
            ) : (
              <div className="mb-6">
                <label className="block text-sm font-bold text-slate-700 mb-2">
                  Discount Amount (₹)
                </label>
                <div className="relative">
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={discountAmount}
                    onChange={(e) => {
                      const num = Number(e.target.value);
                      const maxDiscount = selectedBill.subtotal + selectedBill.cgst + selectedBill.sgst;
                      if (!isNaN(num) && num >= 0 && num <= maxDiscount) {
                        setDiscountAmount(num);
                      }
                    }}
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none font-medium"
                    placeholder="e.g., 50"
                  />
                  <div className="absolute right-3 top-3 text-slate-400 font-bold">₹</div>
                </div>
                <p className="text-xs text-slate-500 mt-1 font-medium">
                  Maximum: ₹{(selectedBill.subtotal + selectedBill.cgst + selectedBill.sgst).toFixed(2)}
                </p>
              </div>
            )}

            <div className="bg-slate-50 rounded-xl p-4 space-y-3 border border-slate-100">
              <div className="flex justify-between text-sm">
                <span className="text-slate-600 font-medium">Subtotal:</span>
                <span className="font-bold">₹{selectedBill.subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-600 font-medium">CGST:</span>
                <span className="font-bold">₹{selectedBill.cgst.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-600 font-medium">SGST:</span>
                <span className="font-bold">₹{selectedBill.sgst.toFixed(2)}</span>
              </div>
              <div className="border-t border-slate-200 pt-3">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600 font-medium">Total before discount:</span>
                  <span className="font-bold">
                    ₹{(selectedBill.subtotal + selectedBill.cgst + selectedBill.sgst).toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between text-sm text-orange-600 mt-2">
                  <span className="font-bold">
                    Discount ({discountType === 'percentage' ? `${discountPercentage}%` : 'Amount'}):
                  </span>
                  <span className="font-bold">
                    -₹{discountType === 'percentage' 
                      ? ((selectedBill.subtotal + selectedBill.cgst + selectedBill.sgst) * discountPercentage / 100).toFixed(2)
                      : discountAmount.toFixed(2)
                    }
                  </span>
                </div>
                <div className="border-t border-slate-200 mt-3 pt-3">
                  <div className="flex justify-between font-extrabold text-lg text-[#4F46E5]">
                    <span>New Total:</span>
                    <span>
                      ₹{(
                        (selectedBill.subtotal + selectedBill.cgst + selectedBill.sgst) -
                        (discountType === 'percentage' 
                          ? ((selectedBill.subtotal + selectedBill.cgst + selectedBill.sgst) * discountPercentage / 100)
                          : discountAmount
                        )
                      ).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <button 
              className="w-full mt-6 py-4 px-6 bg-[#4F46E5] hover:bg-[#4338CA] text-white rounded-xl font-bold shadow-lg shadow-indigo-100 transition-all transform hover:scale-[1.02] active:scale-95"
              onClick={handleSaveBillDetails}
            >
              Save Details
            </button>
          </div>
        </div>
      </div>
    )
  );

  const getStatusStyle = (status) => {
    switch (status) {
      case 'COMPLETED':
        return 'bg-emerald-50 text-emerald-600 border-emerald-100';
      case 'PENDING':
        return 'bg-amber-50 text-amber-600 border-amber-100';
      default:
        return 'bg-slate-50 text-slate-600 border-slate-100';
    }
  };

  return (
    <>
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-[1800px] mx-auto px-6 sm:px-8 py-6">
          
          {/* Welcome Section */}
          <div className="flex items-end justify-between mb-8 animate-in fade-in duration-500">
            <div>
              <p className="text-sm font-bold text-slate-400 mb-1">{currentDate}</p>
              <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight">
                {restaurantInfo?.name || 'Good Morning!'}
              </h1>
              {restaurantInfo && (
                <div className="flex flex-wrap items-center text-slate-500 gap-4 mt-2 text-sm">
                  {restaurantInfo.phoneNumber && (
                    <div className="flex items-center">
                      <Phone size={14} className="mr-1.5 text-[#4F46E5]" />
                      <span className="font-medium">{restaurantInfo.phoneNumber}</span>
                    </div>
                  )}
                  {restaurantInfo.gstin && (
                    <div className="flex items-center">
                      <Hash size={14} className="mr-1.5 text-[#4F46E5]" />
                      <span className="font-medium">GSTIN: {restaurantInfo.gstin}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-all">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <p className="text-slate-500 text-sm font-bold">Today's Revenue</p>
                  <h3 className="text-3xl font-extrabold text-slate-900 mt-2">₹{todayRevenue.toFixed(2)}</h3>
                </div>
                <div className="p-3 rounded-xl bg-indigo-50">
                  <DollarSign className="text-[#4F46E5]" size={24} />
                </div>
              </div>
              <div className="flex items-center gap-1.5 text-xs font-bold text-emerald-500">
                <TrendingUp size={14} />
                {completedBills} completed transactions
              </div>
            </div>

            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-all">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <p className="text-slate-500 text-sm font-bold">Avg. Transaction</p>
                  <h3 className="text-3xl font-extrabold text-slate-900 mt-2">₹{averageTransaction.toFixed(2)}</h3>
                </div>
                <div className="p-3 rounded-xl bg-purple-50">
                  <Receipt className="text-purple-600" size={24} />
                </div>
              </div>
              <div className="flex items-center gap-1.5 text-xs font-bold text-slate-500">
                Per completed transaction
              </div>
            </div>

            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-all">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <p className="text-slate-500 text-sm font-bold">Completed Bills</p>
                  <h3 className="text-3xl font-extrabold text-slate-900 mt-2">{completedBills}</h3>
                </div>
                <div className="p-3 rounded-xl bg-orange-50">
                  <CheckCircle className="text-orange-500" size={24} />
                </div>
              </div>
              <div className="flex items-center gap-1.5 text-xs font-bold text-slate-500">
                Out of {bills.length} total bills
              </div>
            </div>

            <div className="bg-[#1E293B] text-white p-6 rounded-2xl flex flex-col justify-between shadow-xl relative overflow-hidden group">
              <div className="absolute top-0 right-0 p-3 opacity-20 transform group-hover:scale-110 transition-transform">
                <Clock size={48} />
              </div>
              <p className="text-slate-400 text-xs font-bold uppercase tracking-wider">Live Time</p>
              <div className="mt-3">
                <h3 className="text-4xl font-extrabold flex items-baseline gap-2">
                  {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true }).split(' ')[0]}
                  <span className="text-xl font-bold opacity-70">{new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true }).split(' ')[1]}</span>
                </h3>
                <p className="text-slate-400 text-xs mt-2 font-medium">{new Date().toLocaleDateString()}</p>
              </div>
            </div>
          </div>

          {/* Bills Table Card */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden mb-8">
            <div className="p-8 flex items-center justify-between border-b border-slate-100">
              <div>
                <h3 className="text-xl font-bold text-slate-800">Today's Bills</h3>
                <p className="text-slate-500 text-sm mt-1 font-medium">Manage and view all transactions</p>
              </div>
              <button 
                className="text-sm font-bold text-[#4F46E5] hover:text-[#4338CA] flex items-center gap-2 transition-colors px-4 py-2"
                onClick={fetchTodaysBills}
              >
                <RefreshCcw size={18} />
                Refresh
              </button>
            </div>
            
            {bills.length === 0 ? (
              <div className="p-16 text-center text-slate-500">
                <div className="mx-auto w-24 h-24 bg-slate-100 rounded-full flex items-center justify-center mb-6">
                  <Calendar size={40} className="text-slate-400" />
                </div>
                <p className="text-xl font-bold text-slate-600">No bills found for today</p>
                <p className="text-slate-400 mt-2 font-medium text-base">Create a new bill to get started</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-100 text-slate-400 text-xs font-bold uppercase tracking-wider">
                      <th className="px-8 py-5">Bill ID</th>
                      <th className="px-8 py-5">Customer</th>
                      <th className="px-8 py-5">Table</th>
                      <th className="px-8 py-5">Time</th>
                      <th className="px-8 py-5">Items</th>
                      <th className="px-8 py-5">Amount</th>
                      <th className="px-8 py-5">Payment</th>
                      <th className="px-8 py-5">Status</th>
                      <th className="px-8 py-5 text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="text-sm font-medium text-slate-600">
                    {bills.map((bill) => (
                      <tr key={bill.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/50 transition-colors group">
                        <td className="px-8 py-5 font-bold text-slate-800 text-base">#{bill.billNumber}</td>
                        <td className="px-8 py-5 text-slate-500">{bill.names || 'Walk-in'}</td>
                        <td className="px-8 py-5 text-slate-400">
                          {(bill.tableId || bill.tableNumber) ? getTableDisplayName(bill.tableId, bill.tableNumber) : '-'}
                        </td>
                        <td className="px-8 py-5 text-slate-400">{formatDate(bill.createdAt)}</td>
                        <td className="px-8 py-5">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold">{bill.items.length} items</span>
                            {renderDiscountBadge(bill)}
                          </div>
                        </td>
                        <td className="px-8 py-5 font-extrabold text-slate-900 text-base">₹{bill.totalAmount.toFixed(2)}</td>
                        <td className="px-8 py-5">
                          {bill.paymentMethod === 'CASH' ? (
                            <button
                              onClick={() => { if (isBillEditable(bill)) { setSelectedBill(bill); setActiveModal(MODAL_TYPES.PAYMENT); } }}
                              className={`flex items-center gap-2 bg-emerald-50 text-emerald-700 border border-emerald-100 px-3 py-1.5 rounded-full w-fit transition-all ${isBillEditable(bill) ? 'hover:bg-emerald-100 hover:shadow-sm cursor-pointer' : 'cursor-default'}`}
                            >
                              <DollarSign size={13} />
                              <span className="text-xs font-bold">CASH</span>
                            </button>
                          ) : bill.paymentMethod === 'CARD' ? (
                            <button
                              onClick={() => { if (isBillEditable(bill)) { setSelectedBill(bill); setActiveModal(MODAL_TYPES.PAYMENT); } }}
                              className={`flex items-center gap-2 bg-indigo-50 text-indigo-700 border border-indigo-100 px-3 py-1.5 rounded-full w-fit transition-all ${isBillEditable(bill) ? 'hover:bg-indigo-100 hover:shadow-sm cursor-pointer' : 'cursor-default'}`}
                            >
                              <CreditCard size={13} />
                              <span className="text-xs font-bold">CARD</span>
                            </button>
                          ) : bill.paymentMethod === 'UPI' ? (
                            <button
                              onClick={() => { if (isBillEditable(bill)) { setSelectedBill(bill); setActiveModal(MODAL_TYPES.PAYMENT); } }}
                              className={`flex items-center gap-2 bg-purple-50 text-purple-700 border border-purple-100 px-3 py-1.5 rounded-full w-fit transition-all ${isBillEditable(bill) ? 'hover:bg-purple-100 hover:shadow-sm cursor-pointer' : 'cursor-default'}`}
                            >
                              <Smartphone size={13} />
                              <span className="text-xs font-bold">UPI</span>
                            </button>
                          ) : (
                            <button
                              onClick={() => { if (isBillEditable(bill)) { setSelectedBill(bill); setActiveModal(MODAL_TYPES.PAYMENT); } }}
                              className={`text-slate-400 text-xs font-medium transition-all ${isBillEditable(bill) ? 'hover:text-slate-600 cursor-pointer' : 'cursor-default'}`}
                            >
                              —
                            </button>
                          )}
                        </td>
                        <td className="px-8 py-5">
                          <span className={`px-3 py-1.5 rounded-full text-xs font-bold border ${getStatusStyle(bill.status)}`}>
                            {bill.status}
                          </span>
                        </td>
                        <td className="px-8 py-5">
                          <div className="flex items-center justify-center gap-5 text-slate-400 opacity-60 group-hover:opacity-100 transition-opacity">
                            <button 
                              className="hover:text-[#4F46E5] transition-colors p-1.5 hover:bg-indigo-50 rounded-lg"
                              onClick={() => handlePrint(bill)}
                            >
                              <Printer size={18} />
                            </button>
                            {isBillEditable(bill) && (
                              <>
                                <button 
                                  className="hover:text-[#4F46E5] transition-colors p-1.5 hover:bg-indigo-50 rounded-lg"
                                  onClick={() => handleEditBill(bill)}
                                >
                                  <Edit2 size={18} />
                                </button>
                                <button 
                                  className="hover:text-orange-500 transition-colors p-1.5 hover:bg-orange-50 rounded-lg"
                                  onClick={() => handleBillDetails(bill)}
                                  title="Edit Discount"
                                >
                                  <Tag size={18} />
                                </button>
                                <button 
                                  className="hover:text-rose-500 transition-colors p-1.5 hover:bg-rose-50 rounded-lg"
                                  onClick={() => handleDeleteBill(bill)}
                                  title="Delete Bill"
                                >
                                  <Trash2 size={18} />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Bottom Grid - Kitchen View, Table Map, and Occupancy */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-20">
            <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Kitchen View Card */}
              <div 
                onClick={() => navigate('/kot')}
                className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm flex flex-col items-center justify-center group cursor-pointer hover:border-indigo-200 hover:shadow-md transition-all"
              >
                <div className="w-20 h-20 bg-indigo-50 text-[#4F46E5] rounded-2xl flex items-center justify-center mb-5 transform group-hover:scale-110 transition-transform shadow-sm">
                  <ChefHat size={32} />
                </div>
                <h3 className="text-lg font-bold text-slate-800">Kitchen View</h3>
              </div>

              {/* Table Map Card */}
              <div 
                onClick={() => navigate('/tables')}
                className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm flex flex-col items-center justify-center group cursor-pointer hover:border-purple-200 hover:shadow-md transition-all"
              >
                <div className="w-20 h-20 bg-purple-50 text-purple-600 rounded-2xl flex items-center justify-center mb-5 transform group-hover:scale-110 transition-transform shadow-sm">
                  <UtensilsCrossed size={32} />
                </div>
                <h3 className="text-lg font-bold text-slate-800">Table Map</h3>
                {tables.length > 0 && (
                  <div className="mt-4 flex gap-3">
                    <span className="bg-purple-50 text-purple-600 px-4 py-1.5 rounded-full text-xs font-bold border border-purple-100">
                      {occupiedTables}/{tables.length} Occupied
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Occupancy Card */}
            <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm flex flex-col justify-center">
              <div className="flex items-center gap-4 mb-8">
                <div className="p-3.5 bg-orange-50 text-orange-500 rounded-xl">
                  <Sofa size={28} />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900 leading-tight">Occupancy</h3>
                  <p className="text-slate-400 text-sm font-medium mt-1">Real-time table status</p>
                </div>
              </div>

              <div className="space-y-7">
                {tables.length > 0 ? (
                  <>
                    <div>
                      <div className="flex justify-between items-center text-sm mb-3">
                        <span className="font-bold text-slate-600">All Tables</span>
                        <span className="font-extrabold text-slate-900 text-base">{occupiedTables}/{tables.length} Tables</span>
                      </div>
                      <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-orange-500 rounded-full transition-all duration-1000"
                          style={{ width: `${tables.length > 0 ? (occupiedTables / tables.length) * 100 : 0}%` }}
                        ></div>
                      </div>
                    </div>

                    <div>
                      <div className="flex justify-between items-center text-sm mb-3">
                        <span className="font-bold text-slate-600">Available</span>
                        <span className="font-extrabold text-emerald-600 text-base">{tables.length - occupiedTables} Tables</span>
                      </div>
                      <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-emerald-500 rounded-full transition-all duration-1000"
                          style={{ width: `${tables.length > 0 ? ((tables.length - occupiedTables) / tables.length) * 100 : 0}%` }}
                        ></div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-6">
                    <p className="text-slate-400 text-sm font-medium">No table data available</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      {renderEditBillModal()}
      {renderMenuModal()}
      {renderVariantModal()}
      {renderPaymentMethodModal()}
      {renderDetailsModal()}
    </>
  );
};

export default Dashboard;
